import { useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import { useVirtualizer } from '@tanstack/react-virtual';
import TextInput from '../form/TextInput';

/* ------------------------------------------------------------------ */
/*  Header filter: checkbox list of the column's distinct values       */
/* ------------------------------------------------------------------ */

export interface FilterValue {
  text: string;
  count: number;
}

export const BLANK_TEXT = '(Blanks)';

interface HeaderFilterProps {
  anchorEl: HTMLElement | null;
  caption: string;
  values: FilterValue[];
  /** Currently allowed values; null = no filter (everything allowed). */
  selected: ReadonlySet<string> | null;
  onApply: (allowed: Set<string> | null) => void;
  onClose: () => void;
}

const ITEM_H = 30;
const VIRTUALIZE_OVER = 200;

function FilterItem({ v, checked, onToggle, style }: { v: FilterValue; checked: boolean; onToggle: (text: string) => void; style?: CSSProperties }) {
  return (
    <label className="app-grid-hf-item" style={style}>
      <input type="checkbox" aria-label={v.text} checked={checked} onChange={() => onToggle(v.text)} />
      <span className="app-grid-hf-text">{v.text}</span>
      <span className="app-grid-hf-count">{v.count.toLocaleString('en-US')}</span>
    </label>
  );
}

/**
 * DevExtreme's header filter: search, Select all, one checkbox per distinct
 * value with its count, OK / Cancel. The list is virtualised, so a column
 * with thousands of distinct values opens as fast as one with five.
 */
export function HeaderFilterPopover({ anchorEl, caption, values, selected, onApply, onClose }: HeaderFilterProps) {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(selected ?? values.map((v) => v.text)));
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? values.filter((v) => v.text.toLowerCase().includes(q)) : values;
  }, [values, query]);

  // Short lists render plainly; long ones (thousands of distinct values) are virtualised.
  const virtualize = shown.length > VIRTUALIZE_OVER;
  const virtualizer = useVirtualizer({
    enabled: virtualize,
    count: shown.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ITEM_H,
    overscan: 8,
    initialRect: { width: 260, height: 300 },
  });

  const allShownChecked = shown.length > 0 && shown.every((v) => checked.has(v.text));
  const someShownChecked = shown.some((v) => checked.has(v.text));

  const toggle = (text: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(text)) next.delete(text);
    else next.add(text);
    return next;
  });

  const toggleAll = () => setChecked((prev) => {
    const next = new Set(prev);
    for (const v of shown) {
      if (allShownChecked) next.delete(v.text);
      else next.add(v.text);
    }
    return next;
  });

  // With a search typed, OK keeps only the ticked values the search shows, as Excel does: values the search hides
  // drop out. Before, they stayed ticked, so typing "Croft", unticking Select all and ticking Crofton still let
  // every value through (Neil, 2026-11-02, in Tech Log's copy of this grid).
  const searching = query.trim() !== '';
  const allowed = useMemo(
    () => (searching ? new Set(shown.filter((v) => checked.has(v.text)).map((v) => v.text)) : checked),
    [searching, shown, checked],
  );

  const apply = () => {
    const all = values.every((v) => allowed.has(v.text));
    onApply(all ? null : allowed);
  };

  const selectAllId = `hf-all-${caption.replace(/\W/g, '')}`;

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { className: 'app-grid-popover app-grid-hf', role: 'dialog', 'aria-label': `Filter ${caption}` } }}
    >
      <div className="app-grid-hf-search">
        <TextInput value={query} onValueChanged={setQuery} ariaLabel="Search" placeholder="Search" mode="search" inputAttr={{ autoFocus: true }} />
      </div>
      <label className="app-grid-hf-item app-grid-hf-all" htmlFor={selectAllId}>
        <input
          id={selectAllId}
          type="checkbox"
          aria-label="Select all"
          checked={allShownChecked}
          ref={(el) => {
            if (el) el.indeterminate = !allShownChecked && someShownChecked;
          }}
          onChange={toggleAll}
        />
        <span>Select all</span>
      </label>
      <div ref={listRef} className="app-grid-hf-list" role="group" aria-label="Values">
        {virtualize ? (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((vi) => {
              const v = shown[vi.index];
              return (
                <FilterItem
                  key={v.text}
                  v={v}
                  checked={checked.has(v.text)}
                  onToggle={toggle}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${vi.start}px)` }}
                />
              );
            })}
          </div>
        ) : shown.map((v) => <FilterItem key={v.text} v={v} checked={checked.has(v.text)} onToggle={toggle} />)}
        {shown.length === 0 && <div className="app-grid-hf-none">No values match</div>}
      </div>
      <div className="app-grid-popover-actions">
        <Button size="small" variant="contained" onClick={apply} disabled={allowed.size === 0}>OK</Button>
        <Button size="small" variant="text" onClick={onClose}>Cancel</Button>
      </div>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/*  Column chooser                                                     */
/* ------------------------------------------------------------------ */

interface ColumnChooserProps {
  anchorEl: HTMLElement | null;
  columns: { id: string; caption: string; visible: boolean; canHide: boolean }[];
  onToggle: (id: string, visible: boolean) => void;
  onClose: () => void;
}

export function ColumnChooserPopover({ anchorEl, columns, onToggle, onClose }: ColumnChooserProps) {
  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: { className: 'app-grid-popover app-grid-cc', role: 'dialog', 'aria-label': 'Columns' },
        // Start on the first checkbox (instead of autoFocus on it).
        transition: { onEntered: (node: HTMLElement) => node.querySelector<HTMLInputElement>('input[type="checkbox"]')?.focus() },
      }}
    >
      <div className="app-grid-cc-title" id="app-grid-cc-title">Columns</div>
      <div className="app-grid-cc-list" role="group" aria-labelledby="app-grid-cc-title">
        {columns.map((c, i) => {
          const id = `app-grid-cc-${i}`;
          return (
            <label key={c.id} className="app-grid-hf-item" htmlFor={id}>
              <input
                id={id}
                type="checkbox"
                aria-label={c.caption || '(No caption)'}
                checked={c.visible}
                disabled={!c.canHide}
                onChange={(e) => onToggle(c.id, e.target.checked)}
              />
              <span>{c.caption || '(No caption)'}</span>
            </label>
          );
        })}
      </div>
      <div className="app-grid-popover-actions">
        <Button size="small" variant="text" onClick={onClose}>Close</Button>
      </div>
    </Popover>
  );
}
